// src/theme/foundations/typography.ts
import { defineTextStyles } from '@chakra-ui/react'

// The application is set in Geist: built for interfaces carrying figures, sober
// and legible down to the small sizes this portal is full of.
//
// Alternatives for body text, in the order we would reach for them:
//
//   Atkinson      Built for legibility, the most rational of the set. Its
//                 slashed zero is conspicuous in a screen full of numbers.
//   TASA Orbiter  Polygran's house face. Strong identity, but the portal
//                 already carries that association through the corporate type;
//                 doubling it costs variety without adding anything.
//
// Host Grotesk is not a body face here: it is beautiful but loses legibility at
// small sizes. It would work as a display face for very large headings, in the
// same role Newsreader was meant for - though pairing either with a different
// sans for body text is a deliberate choice, not a default.
//
// Newsreader stays disabled. The `heading` token is kept and points at the same
// family as `body`, so every semantic call site (textStyles, recipes, the h1/h2
// rule) stays correct and restoring an accent is a change here plus the matching
// font import in src/theme/index.ts. See CLAUDE.md for the accent boundary.
//
// const newsreader = "'Newsreader Variable', Georgia, serif"
// const hostGrotesk = "'Host Grotesk Variable', system-ui, sans-serif"
// const tasaOrbiter = "'TASA Orbiter Variable', system-ui, sans-serif"
// const atkinson = "'Atkinson Hyperlegible Next Variable', system-ui, sans-serif"
const geist = "'Geist Variable', system-ui, sans-serif"

export const fontTokens = {
  heading: { value: geist },
  body: { value: geist },
}

export const fontSizeTokens = {
  '5xl': { value: '3.125rem' },
}

export const textStyles = defineTextStyles({
  display: {
    value: {
      fontFamily: 'heading',
      fontWeight: 'medium',
      fontSize: '5xl',
      lineHeight: '1.1',
    },
  },
  pageTitle: {
    value: {
      fontFamily: 'heading',
      fontWeight: 'medium',
      fontSize: '4xl',
      lineHeight: '2.75rem',
    },
  },
  sectionTitle: {
    value: {
      fontFamily: 'heading',
      fontWeight: 'medium',
      fontSize: '3xl',
      lineHeight: '2.375rem',
    },
  },
  sectionTitleItalic: {
    value: {
      fontFamily: 'heading',
      fontWeight: 'medium',
      fontStyle: 'italic',
      fontSize: '3xl',
      lineHeight: '2.375rem',
    },
  },
  featureTitle: {
    value: {
      fontFamily: 'heading',
      fontWeight: 'medium',
      fontSize: '2xl',
      lineHeight: '2rem',
    },
  },
  // Cards and smaller surfaces stay in TASA Orbiter; Newsreader starts at 2xl.
  cardTitle: {
    value: {
      fontFamily: 'body',
      fontWeight: 'medium',
      fontSize: 'xl',
      lineHeight: '1.875rem',
    },
  },
  highlight: {
    value: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: 'xl',
      lineHeight: '1.875rem',
    },
  },
  subtitle: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: 'lg',
      lineHeight: '1.75rem',
    },
  },
  bodyLarge: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: 'md',
      lineHeight: '1.5rem',
    },
  },
  body: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: 'sm',
      lineHeight: '1.25rem',
    },
  },
  metadata: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: 'xs',
      lineHeight: '1rem',
    },
  },
  micro: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: '2xs',
      lineHeight: '0.75rem',
    },
  },
  financialValue: {
    value: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: 'md',
      lineHeight: '1.5rem',
      fontVariantNumeric: 'tabular-nums',
    },
  },
  '3xlMedium': {
    value: {
      fontFamily: 'body',
      fontWeight: 'medium',
      fontSize: '3xl',
      lineHeight: '38px',
    },
  },
  '2xlMedium': {
    value: {
      fontFamily: 'body',
      fontWeight: 'medium',
      fontSize: '2xl',
      lineHeight: '32px',
    },
  },
  xlSemibold: {
    value: {
      fontFamily: 'body',
      fontWeight: 'semibold',
      fontSize: 'xl',
      lineHeight: '30px',
    },
  },
  lgSemibold: {
    value: {
      fontFamily: 'body',
      fontWeight: 'semibold',
      fontSize: 'lg',
      lineHeight: '28px',
    },
  },
  smNormal: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: 'sm',
      lineHeight: '20px',
    },
  },
  lgNormal: {
    value: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: 'lg',
      lineHeight: '28px',
      color: 'fg',
    },
  },
  smBold: {
    value: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: 'sm',
      lineHeight: '20px',
    },
  },
  smMedium: {
    value: {
      fontFamily: 'body',
      fontWeight: 'semibold',
      fontSize: 'sm',
      lineHeight: '20px',
    },
  },
  smSemibold: {
    value: {
      fontFamily: 'body',
      fontWeight: 'semibold',
      fontSize: 'sm',
      lineHeight: '20px',
    },
  },
  '2xsMedium': {
    value: {
      fontFamily: 'body',
      fontWeight: '500',
      fontSize: '2xs',
      lineHeight: '14px',
    },
  },
  xsMedium: {
    value: {
      fontFamily: 'body',
      fontWeight: '500',
      fontSize: 'xs',
      lineHeight: '16px',
    },
  },
  mdMedium: {
    value: {
      fontFamily: 'body',
      fontWeight: '500',
      fontSize: 'md',
      lineHeight: '24px',
    },
  },
})
